import { getPayerIdentity } from "@/providers/firebase/authProvider";

/**
 * stripeNavigation — ONE way to hand a customer to Stripe. Gap-analysed against glowtest_FE, the
 * reference this surface was ported from and the one that does NOT have the defect.
 *
 * ═══ THE GAP, MEASURED (2026-09-25) ═══
 *
 * | | glowtest_FE (reference) | raisy_FE before | raisy_FE, first attempt |
 * |---|---|---|---|
 * | where the link comes from | on the plan object — ALREADY KNOWN when he clicks | plans: known · top-up: ASKED OF OUR BACKEND after the click · portal: ASKED OF STRIPE | unchanged |
 * | how it opens | `window.open(url, "_blank").focus()` INSIDE the click | the same call, but for the top-up/portal it ran AFTER an `await` | `window.location.assign(url)` — replaced the whole tab |
 * | can a browser refuse it | NO — the click's activation is still live | YES for the top-up + portal (Chrome did) | no |
 * | does he stay in the app | YES — new tab, app untouched | yes when not blocked | NO — the app was gone |
 *
 * SO THE DEFECT WAS NEVER "A POPUP" — it was the `await` between the click and the open. `window.open`
 * works only while the click's user-activation is live; our top-up creates its Checkout Session per
 * amount and our portal asks Stripe for a session, so by the time the link arrived the activation had
 * expired and Chrome treated the open as unsolicited: `Pop-ups blocked: …stripe.com/…/cs_live_…` (the
 * PO's own browser, raisy.team). The plan buttons had their link already — which is why they worked,
 * and why the defect looked intermittent.
 *
 * ═══ WHAT STANDS HERE, AND WHY ═══
 *
 * `handToStripe` opens the tab INSIDE the click — before anything is awaited, so the browser permits it —
 * and fills it when the link arrives (measured: 250 ms for the top-up's session, so the blank moment is
 * imperceptible, and the tab states what it is doing rather than showing nothing). The app stays exactly
 * where the customer was: the reference behaviour, and the one he asked for.
 *
 * IF THE TAB CANNOT OPEN — a browser or extension with pop-ups blocked for this site, the very setting
 * the PO's dialog offered him — `window.open` returns null. That is DETECTABLE, so it is not a dead end:
 * the app navigates in its own tab and he still pays. The block is impossible in both directions: a tab
 * when the browser allows one, the same tab when it does not.
 *
 * ═══ THE NUMBERS THIS RESTS ON (measured, 2026-09-25) ═══
 *  · the top-up's session takes **0.24-0.30 s** on the deployed DEV backend (three runs: .30 .25 .24;
 *    the portal .27) — which is why opening the tab first costs the customer nothing perceptible, and
 *    why the tab says what it is doing instead of showing blank.
 *  · the build's own gate is plain `tsc`: 0 errors, exit 0. (`tsc --project tsconfig.app.json` reports
 *    ~2,260 pre-existing errors and is NOT what the build runs — quoting that number as a blocker is how
 *    a clean change gets read as a broken build.)
 *  · the DEV front end did not rebuild for 34 minutes after this landed (the served bundle stayed
 *    `index-DpncmXyw.js` while `origin/dev` already carried `handToStripe`), so the push below re-triggers
 *    it. Worth knowing when a fix "is not live yet": read the SERVED BUNDLE for a string your change
 *    introduced, never the git log.
 *
 * AND A PRESS THAT CANNOT OPEN ANYTHING STILL SAYS SO on the surface that was pressed — an earlier
 * version returned silently, which is the "clickable and does nothing" class.
 */

/** What comes back, so the caller can tell the customer when there was nothing to open. */
export type StripeHandoff = { ok: true } | { ok: false; reason: string };

/**
 * The URL to send the customer to, with HIS identity on it.
 *
 * THE IDENTITY IS NOT DECORATION. glowtest_FE appends the payer's email to every Stripe link so the
 * payment can be matched back to an account; a bare link leaves the only identity to whatever the buyer
 * types at checkout. We copy that and add the stronger half — `client_reference_id` = the Firebase uid,
 * the exact id our webhook grants to, which cannot be mistyped and does not change if he pays from a
 * different address. No signed-in user → the bare link, the same fallback glowtest uses.
 */
export function stripeCheckoutUrl(url: string): string | null {
  if (!url) return null;
  const { uid, email } = getPayerIdentity();
  try {
    const decorated = new URL(url);
    if (uid) decorated.searchParams.set("client_reference_id", uid);
    if (email) decorated.searchParams.set("prefilled_email", email);
    return decorated.toString();
  } catch {
    // A link we cannot parse is opened exactly as the config kernel gave it to us.
    return url;
  }
}

/** What the customer sees in the tab during the ~250 ms before Stripe's page arrives. A blank tab reads
 *  as broken; a sentence reads as "this is happening". */
const OPENING_HTML =
  '<!doctype html><title>Opening the payment page…</title>' +
  '<body style="margin:0;font:14px system-ui,-apple-system,Segoe UI,sans-serif;color:#475569;' +
  'display:flex;align-items:center;justify-content:center;height:100vh">Opening the payment page…</body>';

/**
 * Send the customer to Stripe with the smallest possible interruption.
 *
 * @param obtain called AFTER the tab is open, so the request runs while he is already on his way; it
 *               returns the checkout URL, or null when there is nothing to open.
 */
export async function handToStripe(obtain: () => Promise<string | null>): Promise<StripeHandoff> {
  // ⚠️ THIS MUST RUN SYNCHRONOUSLY, BEFORE ANY `await` — it is the entire fix. Opening the tab inside
  // the click is what keeps the user-activation alive, and therefore what makes the browser permit it.
  let tab: Window | null = null;
  try {
    tab = window.open("", "_blank");
    if (tab) {
      // We hold the reference so we can point it at Stripe; clearing `opener` BEFORE it leaves for a
      // cross-origin page keeps the protection `noopener` would have given us.
      try {
        tab.opener = null;
      } catch {
        /* some browsers refuse the assignment; the reference we hold is same-origin either way */
      }
      try {
        tab.document.write(OPENING_HTML);
        tab.document.close();
      } catch {
        /* failing to paint the placeholder must never stop the payment */
      }
    }
  } catch {
    tab = null;
  }

  const url = await obtain().catch(() => null);
  const target = url ? stripeCheckoutUrl(url) : null;

  if (!target) {
    // Nothing to open: close the tab we took so he is not left with a dead one, and say why.
    try {
      tab?.close();
    } catch {
      /* already gone */
    }
    return { ok: false, reason: "The payment page could not be opened. Nothing was charged." };
  }

  if (tab) {
    tab.location.href = target;
    try {
      tab.focus();
    } catch {
      /* focusing is a courtesy, not the payment */
    }
    return { ok: true };
  }

  // THE FALLBACK: no tab was allowed, so take the one we already have. He still pays — which is the
  // point — and this is the only case where he leaves the app.
  window.location.assign(target);
  return { ok: true };
}
